import _, { isNil, upperCase } from 'lodash';
import moment, { Moment } from 'moment';

import {
  RegistrationSituationStatus,
  RenewalOrderStatus,
} from '@appTypes/enums';
import type {
  OrgAdultsRoster,
  OrgSubUnit,
  OrgYouthsRoster,
  RegistrarInfo,
  RosterParent,
} from '@appTypes/esb';
import type {
  NewRosterParent,
  NewRosterParentInformation,
} from '@appTypes/esb/organizations/v2/units/_orgGuid/parents';
import { PositionId, denTypesNames, programNames } from '@shared/constants';

import type { RosterScout } from '../duck/types';
import {
  PACK_ROSTER_FILTERS,
  YOUTH_MEMBER,
  renewalStatuses,
} from './constants';
import type {
  RosterSlice,
  StoredPackRosterFilters,
  SubUnitFilterTag,
} from './types';

export const getIsDenChief = (scout: RosterScout) =>
  scout.positions?.some(({ positionId }) => positionId === PositionId.DenChief);

export const dedupeMembers = (scouts: RosterScout[]) =>
  Object.values(
    scouts.reduce((acc, scout) => {
      const { personGuid, userId } = scout;
      const key = `${personGuid}-${userId}`;
      return {
        ...acc,
        [key]: { ...acc[key], ...scout },
      };
    }, {} as Record<string, RosterScout>),
  );

/**
 * Given that a person could have multiple registrations, this function
 * centralizes the source of truth of which is the "latest" one.
 **/
export function findRegistrationRecord<
  T extends {
    personGuid: string;
    registrationExpireDt: string | Moment;
    registrationEffectiveDt: string | Moment;
  },
>(items: T[], personGuid: string) {
  const person = _.chain(items)
    .filter(item => item.personGuid === personGuid)
    .orderBy(
      ['registrationExpireDt', 'registrationEffectiveDt'],
      ['desc', 'desc'],
    )
    .first()
    .value() as T;

  return person;
}

export const getRegistrationStatus = (registrarInfo: RegistrarInfo) => {
  if (registrarInfo.isLapsed) return RegistrationSituationStatus.Lapsed;
  if (registrarInfo.isExpired) return RegistrationSituationStatus.Expired;
  return RegistrationSituationStatus.Active;
};

export const getMembersRenewalObject = (
  orgInfo: OrgAdultsRoster | OrgYouthsRoster,
) => {
  if (!orgInfo?.members) return [];

  return orgInfo.members.map(
    ({ registrarInfo, personFullName, personGuid }) => {
      const renewalStatus = registrarInfo?.renewalStatus;
      const registrationSituationStatus = getRegistrationStatus(registrarInfo);

      return {
        personFullName,
        personGuid,
        renewalStatus,
        registrationSituationStatus,
        bsaOrderId: registrarInfo.bsaOrderId,
        isYearlyMembershipRenewalPaid:
          registrarInfo.isYearlyMembershipRenewalPaid,
        registrationExpireDt: moment(registrarInfo.registrationExpireDt),
        registrationEffectiveDt: moment(registrarInfo.registrationEffectiveDt),
        registrationId: registrarInfo.registrationId,
        isExpired: registrarInfo.isExpired,
      };
    },
  );
};

export function checkHasOrderStatusFilter(filter: Record<string, boolean>) {
  // By default other filters work like that, if all not checked is as if the filter doesn't matter
  const checks = Object.keys(RenewalOrderStatus).map(
    key => filter[`order_status_${key}`],
  );
  const isAllActive = checks.every(active => active);
  const isAllInactive = checks.every(active => !active);

  return !(isAllActive || isAllInactive);
}

export function canOptOut(
  record: ReturnType<typeof getMembersRenewalObject>[number],
) {
  const { renewalStatus, bsaOrderId } = record;

  if (renewalStatus === renewalStatuses.OPTED_OUT) return false;

  const hasExpired = record.isExpired === true;

  // See SBL-5481
  if (hasExpired && bsaOrderId) {
    return false;
  }

  // See SBL-5481
  if (
    moment().isAfter(record.registrationEffectiveDt) &&
    moment().isBefore(moment(record.registrationEffectiveDt).add(1, 'day'))
  ) {
    return false;
  }

  return true;
}

export const retrieveStoredFilters = ():
  | Record<string, StoredPackRosterFilters>
  | Record<string, never> => {
  const savedFilter = localStorage.getItem(PACK_ROSTER_FILTERS);
  if (savedFilter) {
    return JSON.parse(savedFilter) as Record<string, StoredPackRosterFilters>;
  }

  return {};
};

export const formatSubUnitFilters = (
  subUnitInfo: OrgSubUnit,
): SubUnitFilterTag => {
  const isAol = subUnitInfo.denType === denTypesNames.aol;
  const denType = subUnitInfo.denType
    ? `${isAol ? upperCase(subUnitInfo.denType) : subUnitInfo.denType} `
    : '';
  const formattedSubUnitFilter = {
    id: subUnitInfo.subUnitId,
    name: `${isAol ? upperCase(denType) : denType}${subUnitInfo.subUnitName}`,
    status: true,
    denType: subUnitInfo.denType,
  };
  return formattedSubUnitFilter;
};

export const formatStoredPackRosterFilters = (
  state: RosterSlice,
): StoredPackRosterFilters => ({
  sorter: state.sorter,
  filter: state.filter,
  showSubUnits: state.showSubUnits,
  subUnitFilterTags: state.subUnitFilterTags,
});

export const updateLocalStorage = (
  updatedFilters: Record<string, StoredPackRosterFilters>,
) => {
  // Ensure we keep only the last 10 keys, preserving most recent ones first
  const trimmedFilters = Object.entries(updatedFilters)
    .reverse()
    .slice(0, 10)
    .reduce((acc, [key, value]) => {
      acc[key] = value;
      return acc;
    }, {} as Record<string, StoredPackRosterFilters>);
  localStorage.setItem(PACK_ROSTER_FILTERS, JSON.stringify(trimmedFilters));
};

export const saveStoredPackRosterFilters = (
  state: RosterSlice,
  orgGuid?: string,
): void => {
  if (isNil(orgGuid)) {
    return;
  }
  const newStoreFiltersPerOrgGuid = formatStoredPackRosterFilters(state);

  const retrievedStoredFilters = retrieveStoredFilters();

  // Add or update the new orgGuid entry
  const updatedFilters: Record<string, StoredPackRosterFilters> = {
    ...retrievedStoredFilters,
    [orgGuid]: newStoreFiltersPerOrgGuid,
  };

  updateLocalStorage(updatedFilters);
};

export const clearStoredPackRosterFilters = (orgGuid: string) => {
  const retrievedStoredFilters = retrieveStoredFilters();
  delete retrievedStoredFilters[orgGuid];
  localStorage.setItem(
    PACK_ROSTER_FILTERS,
    JSON.stringify(retrievedStoredFilters),
  );
};

export const replacements: [string | RegExp, string][] = [
  [/,/g, ', '],
  [programNames.BOY_SCOUT, YOUTH_MEMBER],
  ['Committee Chairman', 'Committee Chair'],
];

export const parentsRosterAdapter = (
  data: NewRosterParent[],
): RosterParent[] => {
  if (!Array.isArray(data) || data.length === 0) {
    return [];
  }

  return data.flatMap(youth =>
    (youth.parentGuardianInformation ?? []).map(
      (parent: NewRosterParentInformation) => ({
        youthMemberId: youth.youthMemberId,
        personGuid: youth.personGuid,
        youthUserId: youth.youthUserId,
        parentUserId: parent.parentGuardianUserId,
        parentInformation: {
          relationshipTypeId: parent.relationshipTypeId,
          personFullName: parent.parentGuardianCommonName,
          memberId: parent.parentGuardianMemberId,
          personGuid: parent.parentGuardianPersonGuid,
          firstName: parent.parentGuardianFirstName,
          lastName: parent.parentGuardianLastName,
          dateOfBirth: parent.parentGuardianDateOfBirth,
          gender: parent.parentGuardianGender,
          email: parent.parentGuardianEmail,
        },
      }),
    ),
  );
};
